import React, { useState, useEffect } from 'react';
import { CalendarCheck, ChevronLeft, ChevronRight } from 'lucide-react';
import { api } from '../api';

const shiftMonth = (ym, delta) => {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const monthLabel = (ym) => {
  const [y, m] = ym.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
};

export default function AdminAttendance() {
  const now = new Date();
  const [month, setMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/attendance-report?month=${month}`).then((r) => setData(r.data));
  }, [month]);

  if (!data) return null;
  const [y, m] = month.split('-').map(Number);
  const days = Array.from({ length: data.days_in_month }, (_, i) => i + 1);
  const dstr = (d) => `${month}-${String(d).padStart(2, '0')}`;

  return (
    <div data-testid="admin-attendance">
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <h3 className="font-display font-bold text-lg text-slate-800 flex items-center gap-2"><CalendarCheck size={17} className="text-emerald-600" /> Attendance</h3>
        <div className="flex items-center gap-1 bg-slate-100 border-2 border-slate-200 rounded-full px-1 py-0.5">
          <button data-testid="attendance-prev-month" onClick={() => setMonth(shiftMonth(month, -1))} className="p-1 rounded-full hover:bg-white text-slate-500"><ChevronLeft size={14} /></button>
          <span className="text-xs font-bold text-slate-700 px-1" data-testid="attendance-month-label">{monthLabel(month)}</span>
          <button data-testid="attendance-next-month" onClick={() => setMonth(shiftMonth(month, 1))} className="p-1 rounded-full hover:bg-white text-slate-500"><ChevronRight size={14} /></button>
        </div>
        <p className="font-hand text-base text-slate-400">login days count as present · absent days skip the daily target</p>
      </div>
      <div className="bg-white border-2 border-slate-200 rounded-xl sticky-shadow p-3 overflow-x-auto scroll-thin" data-testid="attendance-grid">
        <table className="border-separate" style={{ borderSpacing: '2px' }}>
          <thead>
            <tr>
              <th className="text-left text-[10px] font-bold uppercase tracking-wider text-slate-400 pr-2 sticky left-0 bg-white">Agent</th>
              {days.map((d) => {
                const sunday = new Date(y, m - 1, d).getDay() === 0;
                return <th key={d} className={`w-6 text-[9px] font-bold ${sunday ? 'text-rose-400' : 'text-slate-400'}`}>{d}</th>;
              })}
              <th className="text-[9px] font-bold text-slate-400 pl-2">In</th>
            </tr>
          </thead>
          <tbody>
            {data.agents.map((a) => {
              const present = new Set(a.present);
              return (
                <tr key={a.agent_id} data-testid={`attendance-row-${a.agent_id}`}>
                  <td className="pr-2 sticky left-0 bg-white">
                    <div className="flex items-center gap-1.5">
                      <span className="w-6 h-6 rounded-full flex items-center justify-center text-[8px] font-extrabold text-slate-800 border border-white shadow-sm shrink-0" style={{ background: a.avatar_color }}>
                        {a.name.split(' ').map((w) => w[0]).join('')}
                      </span>
                      <span className="text-[11px] font-bold text-slate-800 whitespace-nowrap">{a.name}</span>
                    </div>
                  </td>
                  {days.map((d) => {
                    const ds = dstr(d);
                    const future = ds > data.today;
                    const isP = present.has(ds);
                    return (
                      <td key={d}
                        title={`${ds} · ${future ? 'upcoming' : isP ? 'Present' : 'Absent'}`}
                        className={`w-6 h-6 rounded text-center text-[10px] font-extrabold ${future ? 'bg-slate-50 text-slate-200' : isP ? 'bg-emerald-100 text-emerald-700' : 'bg-red-50 text-red-300'}`}>
                        {future ? '' : isP ? '✓' : '·'}
                      </td>
                    );
                  })}
                  <td className="pl-2 text-[10px] font-extrabold text-emerald-700 whitespace-nowrap" data-testid={`attendance-count-${a.agent_id}`}>{a.present.length}d</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <p className="text-[10px] text-slate-400 mt-2 flex items-center gap-3">
          <span><span className="inline-block w-3 h-3 rounded bg-emerald-100 align-middle mr-1" />Present</span>
          <span><span className="inline-block w-3 h-3 rounded bg-red-50 align-middle mr-1" />Absent</span>
          <span><span className="inline-block w-3 h-3 rounded bg-slate-50 border border-slate-100 align-middle mr-1" />Upcoming</span>
        </p>
      </div>
    </div>
  );
}
